// filepath: apps/web/src/features/lists/kanban/KanbanColumn.tsx
//
// FR-UI-VIEW-1 AC-5 — single Kanban column surface.
//
// One column represents ONE enum value of the group_by field (e.g.
// "open", "in_progress", "closed"). The column is a vertical list of
// <KanbanCard /> instances plus a header (label + count + accent dot)
// + a drop zone wired into the HTML5 drag-drop state machine via
// `useKanbanDragDrop`.
//
// SECURITY / INVARIANT REMINDERS:
//   - INV-2 — only records the server emitted are listed. Missing
//     records (server-stripped) are absent; the canvas never re-
//     fetches them.
//   - INV-3 / Drag-drop — column-level `onDragOver` calls
//     `event.preventDefault()` to register the column as a valid
//     drop target. Without this, the browser rejects the drop.
//   - The accent dot is informational only (column identity +
//     at-a-glance scan); it does NOT carry any data the server
//     controls, so the renderer can choose the colour freely.

import React from "react";
import type { NormalisedRecord, WireFieldDefinition } from "../types";
import { KanbanCard, type KanbanRecord } from "./KanbanCard";

export interface KanbanColumnProps {
  /** The enum value this column represents (e.g. "open"). */
  value: string;
  /** Human-readable label (default: `value`). */
  label: string;
  /** Records whose `group_by` field == `value`. Server-stripped. */
  records: readonly KanbanRecord[];
  /** Accent dot colour (CSS value). Free-form — visual only. */
  accentColor: string;
  /**
   * Definition-driven field order passed through to each card.
   * Shared across all columns so cards render the same field set
   * in the same order (AC-2 — column header reads as a label, the
   * cards read as content within the same data model).
   */
  fields?: readonly WireFieldDefinition[];
  /** The enum field the Kanban groups by. */
  groupByField: string;
  /** Drag-start handler (wired from `useKanbanDragDrop`). */
  onCardDragStart: (
    event: React.DragEvent<HTMLElement>,
    payload: {
      recordId: string;
      fromValue: string;
      groupByField: string;
      rowVersion: number;
    },
  ) => void;
  /** Drag-over handler (wired from `useKanbanDragDrop`). */
  onDragOver: (event: React.DragEvent<HTMLElement>) => void;
  /** Drop handler (wired from `useKanbanDragDrop`). */
  onDrop: (event: React.DragEvent<HTMLElement>) => void;
  /** Optional optimistic per-card column overrides. */
  optimisticColumnValue?: (recordId: string) => string | undefined;
  /** Test hook forwarded onto the rendered root. */
  "data-testid"?: string;
}

/**
 * KanbanColumn — single column surface. Layout:
 *   <section role="region" aria-label="<label> column">
 *     <header><dot/> <label> <count/></header>
 *     <ul drop zone> per record: <KanbanCard /> </ul>
 *   </section>
 *
 * The drop zone is the `<ul>`; the section header is non-dropable
 * (HTML5 supports that out-of-the-box). The `onDragOver` opt-in is
 * applied to the `<ul>` (NOT the section) so dragging over the
 * header doesn't trigger the drop detection — which would let the
 * user drop "into the header" and bypass the columns visual
 * feedback.
 */
export function KanbanColumn({
  value,
  label,
  records,
  accentColor,
  fields,
  groupByField,
  onCardDragStart,
  onDragOver,
  onDrop,
  optimisticColumnValue,
  "data-testid": testId = "kanban-column",
}: KanbanColumnProps): React.ReactElement {
  return (
    <section
      className="kanban-column"
      role="region"
      aria-label={`${label} column`}
      data-testid={`${testId}-${value}`}
      data-kanban-column-value={value}
      style={{
        display: "flex",
        flexDirection: "column",
        background: "var(--surface-card)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-md)",
        minWidth: "var(--kanban-column-min-width)",
        flex: "1 1 var(--kanban-column-flex-basis)",
        maxWidth: "var(--kanban-column-max-width)",
        height: "100%",
      }}
    >
      <header
        className="kanban-column-header"
        data-testid={`${testId}-${value}-header`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "var(--space-3)",
          borderBottom: "1px solid var(--border-default)",
          fontFamily: "var(--font-sans)",
          fontSize: "var(--text-sm)",
          fontWeight: "var(--font-medium)",
          color: "var(--text-primary)",
        }}
      >
        <span
          aria-hidden="true"
          data-testid={`${testId}-${value}-accent`}
          style={{
            display: "inline-block",
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: accentColor,
            flex: "0 0 10px",
          }}
        />
        <span className="kanban-column-label">{label}</span>
        <span
          aria-hidden="true"
          data-testid={`${testId}-${value}-count`}
          className="kanban-column-count"
          style={{
            marginLeft: "auto",
            color: "var(--text-tertiary)",
            fontSize: "var(--text-xs)",
            background: "var(--surface-sunken)",
            padding: "2px 8px",
            borderRadius: "var(--radius-pill, 999px)",
          }}
        >
          {records.length}
        </span>
      </header>
      <ul
        className="kanban-column-body"
        data-testid={`${testId}-${value}-body`}
        role="list"
        onDragOver={onDragOver}
        onDrop={onDrop}
        style={{
          listStyle: "none",
          padding: "var(--space-3)",
          margin: 0,
          flex: "1 1 auto",
          overflowY: "auto",
          background: "var(--surface-page)",
          minHeight: "120px",
        }}
      >
        {records.length === 0 ? (
          <li
            data-testid={`${testId}-${value}-empty`}
            className="kanban-column-empty"
            style={{
              padding: "var(--space-4)",
              textAlign: "center",
              color: "var(--text-tertiary)",
              fontFamily: "var(--font-sans)",
              fontSize: "var(--text-xs)",
              border: "1px dashed var(--border-default)",
              borderRadius: "var(--radius-sm)",
            }}
          >
            No records in this column.
          </li>
        ) : null}
        {records.map((record) => (
          <KanbanCard
            key={record.id}
            record={record}
            fields={fields}
            groupByField={groupByField}
            columnValue={optimisticColumnValue?.(record.id)}
            onDragStart={onCardDragStart}
          />
        ))}
      </ul>
    </section>
  );
}

// Re-export KanbanRecord so the board can typecheck the column
// payload without re-importing through KanbanCard.
export type { KanbanRecord } from "./KanbanCard";
// Type-only — used by the board to type the optimistic state. The
// runtime Record type stays in `@/features/lists/types`.
export type { NormalisedRecord };
